/**
 * CrispChat Component
 *
 * Integrates Crisp live chat widget into the application.
 * Loads the Crisp script and initializes with the website ID.
 *
 * This provides live human support alongside the AI-powered help system.
 */

import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Crisp Website ID from your Crisp dashboard
const CRISP_WEBSITE_ID = '23bd4397-f23d-4c5b-ac1f-e381bd1e7af3';

// Extend Window interface for Crisp
declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

export default function CrispChat() {
  const { user, organization, isAuthenticated } = useAuth();

  useEffect(() => {
    // Initialize Crisp configuration
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

    // Load Crisp script
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      // Remove the script
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      // Clean up Crisp elements
      const crispElements = document.querySelectorAll('[class^="crisp"]');
      crispElements.forEach((el) => el.remove());
    };
  }, []);

  // Set user data when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user || !window.$crisp) {
      return;
    }

    // Wait for Crisp to be ready
    const setCrispUser = () => {
      if (typeof window.$crisp === 'object' && Array.isArray(window.$crisp)) {
        // Set user email
        if (user.email) {
          window.$crisp.push(['set', 'user:email', user.email]);
        }

        // Set user nickname (name)
        const fullName = [user.profile?.firstName, user.profile?.lastName]
          .filter(Boolean)
          .join(' ');
        if (fullName) {
          window.$crisp.push(['set', 'user:nickname', fullName]);
        }

        // Set custom data for support context
        window.$crisp.push([
          'set',
          'session:data',
          [
            ['user_id', user._id || ''],
            ['organization_id', organization?._id || ''],
            ['role', user.role || ''],
            ['subscription_tier', organization?.subscription?.tier || 'free'],
          ],
        ]);
      }
    };

    // Try immediately and also after a short delay (in case Crisp hasn't loaded yet)
    setCrispUser();
    const timeout = setTimeout(setCrispUser, 2000);

    return () => clearTimeout(timeout);
  }, [isAuthenticated, user, organization]);

  // This component doesn't render anything visible
  // Crisp handles its own UI
  return null;
}
